import { SxProps, Theme, useTheme } from "@mui/material";
import { createSxStyles } from "../../createSxStyles";
import { IDotLegalTabs } from "./DotLegalTabs";

export type dotLegalTabStyles = "primary" | "secondary" | "paper-tabs";

// Not the theme's fontWeightBold: that went down to 600 platform-wide, and tabs were asked to stay at the bolder 700.
const tabFontWeight = 700;

export const useDotLegalTabsStyles = (props: IDotLegalTabs) => {
    const theme = useTheme();

    return createSxStyles({
        selectedTab: {
            ...getStyle(theme, props.ignoreTextTransformation, props.tabStyle, props.border),
            "& .MuiTabs-scroller": {
                display: props.fullWidth ? "inline-block" : "flex",
            },
            "& .MuiTabs-list": {
                display: "grid",
                gridTemplateColumns: `repeat(${props.content.length}, 1fr)`,
            },
            "& .MuiTabScrollButton-root": {
                width: "unset",
                ...(props.tabStyle === "paper-tabs" && {
                    backgroundColor: theme.customPalette.white,
                    boxShadow: "0px 3px 10px #E7E7E7",
                    borderBottom: "1px solid " + theme.palette.primary.light,
                    "&.Mui-disabled": {
                        display: "none",
                    },
                }),
            },
            "& .MuiTabScrollButton-root:first-child": {
                marginRight: props.tabStyle === "paper-tabs" ? 0 : "8px",
                ...(props.tabStyle === "paper-tabs" && {
                    borderTopLeftRadius: 20,
                }),
            },
            "& .MuiTabScrollButton-root:last-child": {
                marginLeft: props.tabStyle === "paper-tabs" ? 0 : "8px",
                ...(props.tabStyle === "paper-tabs" && {
                    borderTopRightRadius: 20,
                }),
            },
            borderBottom: props.divider ? "1px solid" : undefined,
            borderColor: props.divider ? "divider" : undefined,
        },
    });
};

function getStyle(theme: Theme, ignoreTextTransformation: boolean | undefined, style: dotLegalTabStyles, border: boolean | undefined): SxProps<Theme> {
    if (style === "secondary") {
        return getSecondaryStyling(theme, ignoreTextTransformation, border);
    } else if (style === "paper-tabs") {
        return getPaperTabStyling(theme, ignoreTextTransformation, border);
    } else {
        return getPrimaryStyling(theme, ignoreTextTransformation, style, border);
    }
}

function getPrimaryStyling(theme: Theme, ignoreTextTransformation: boolean | undefined, style: dotLegalTabStyles, border: boolean | undefined): SxProps<Theme> {
    const isPrimary = style === "primary";
    return {
        "& .MuiTab-textColorPrimary": {
            borderTop: border ? "1px solid " + theme.palette.primary.light : undefined,
            borderBottom: "1px solid " + theme.palette.primary.light,
            textTransform: ignoreTextTransformation ? "inherit" : "capitalize",
        },
        "& .MuiTab-textColorPrimary.topLeft": {
            borderTopLeftRadius: 20 + "px",
            borderLeft: border ? "1px solid " + theme.palette.primary.light : undefined,
        },
        "& .MuiTab-textColorPrimary.topRight": {
            borderTopRightRadius: 20 + "px",
            borderRight: border ? "1px solid " + theme.palette.primary.light : undefined,
        },
        "& .MuiTab-textColorPrimary:not(.topLeft)": {
            borderLeft: "1px solid " + theme.palette.primary.light,
        },
        "& .MuiTab-textColorPrimary.Mui-selected": {
            backgroundColor: theme.palette.primary.light,
            outline: "2px solid " + theme.palette.primary.main,
            outlineOffset: "-2px",
            color: isPrimary ? theme.palette.primary.main : theme.palette.primary.dark,
        },
        "& .MuiTab-root": {
            fontWeight: tabFontWeight,
            color: isPrimary ? theme.palette.primary.main : theme.palette.grey[500],
            flexDirection: "row-reverse",
            minWidth: isPrimary ? "unset" : 115,
            backgroundColor: isPrimary ? "unset" : theme.customPalette.white,
        },
        "& .MuiTabs-indicator": {
            display: "none",
        },
        "& .MuiTouchRipple-root": {
            color: theme.palette.primary.main,
        },
    };
}

function getSecondaryStyling(theme: Theme, ignoreTextTransformation: boolean | undefined, border: boolean | undefined): SxProps<Theme> {
    return {
        "& .MuiTab-textColorPrimary": {
            borderTop: border ? "1px solid " + theme.palette.primary.light : undefined,
            borderBottom: border ? "1px solid " + theme.palette.primary.light : undefined,
            textTransform: ignoreTextTransformation ? "inherit" : "capitalize",
            minWidth: 200 + "px",
        },
        "& .Mui-disabled": {
            pointerEvents: "auto",
        },
        "& .MuiTab-root": {
            fontWeight: tabFontWeight,
            color: theme.palette.grey[500],
            "&.Mui-selected": {
                color: theme.palette.primary.dark,
            },
            flexDirection: "row-reverse",
            minHeight: "48px",
        },
        "& .MuiTouchRipple-root": {
            color: theme.palette.primary.main,
        },
        "& .MuiTab-iconWrapper": {
            marginBottom: 0 + "!important",
        },
        "& .MuiTab-textColorPrimary.topLeft": {
            borderTopLeftRadius: 10 + "px",
            borderLeft: border ? "1px solid " + theme.palette.primary.light : undefined,
        },
        "& .MuiTab-textColorPrimary.topRight": {
            borderTopRightRadius: 10 + "px",
            borderRight: border ? "1px solid " + theme.palette.primary.light : undefined,
        },
        "& .MuiTab-textColorPrimary:not(.topLeft)": {
            borderLeft: border ? "1px solid " + theme.palette.primary.light : undefined,
        },
    };
}

function getPaperTabStyling(theme: Theme, ignoreTextTransformation: boolean | undefined, border: boolean | undefined) {
    return {
        ...getPrimaryStyling(theme, ignoreTextTransformation, "paper-tabs", border),
        "& .MuiButtonBase-root": {
            padding: "12px 8px",
        },
        "& .MuiTabs-flexContainer": {
            boxShadow: "0px 3px 10px #E7E7E7",
            display: "flex", // Helps placing boxshadow

            borderTopRightRadius: 20,
            borderTopLeftRadius: 20,
        },
        "& .MuiTabs-scroller": {
            display: "flex", // Helps placing boxshadow
        },
        "& .MuiTabs-list": {
            borderTopRightRadius: 20,
            borderTopLeftRadius: 20,
        },
    };
}
